(() => {
/**
 * Слайдер секции «Наши услуги».
 * Прокрутку делает браузер (scroll-snap), скрипт только двигает трек к соседней
 * карточке и держит состояние стрелок в актуальном виде.
 */

/** Погрешность в пикселях: субпиксельный scrollLeft не должен считаться «не в начале». */
const SCROLL_EPSILON = 2;

const DIRECTION_NEXT = 1;

/** Пауза между wheel-событиями, после которой считаем, что начался новый жест. */
const WHEEL_GESTURE_GAP_MS = 300;
/** Накопленный вертикальный сдвиг колеса, после которого листаем на карточку. */
const WHEEL_STEP_THRESHOLD_PX = 40;
/** Пауза после шага: гасит инерционный хвост тачпада, чтобы один флик не пролистал полтрека. */
const WHEEL_STEP_COOLDOWN_MS = 400;
/** Высота строки для deltaMode=DOM_DELTA_LINE: Firefox шлёт колесо строками, не пикселями. */
const WHEEL_LINE_HEIGHT_PX = 16;

const WHEEL_AXIS_HORIZONTAL = 'x';
const WHEEL_AXIS_VERTICAL = 'y';

/** Приводит deltaY к пикселям независимо от deltaMode. */
function normalizeWheelDelta(event) {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * WHEEL_LINE_HEIGHT_PX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * WHEEL_STEP_THRESHOLD_PX;
  return event.deltaY;
}

class ServicesSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-services-track]');
    this.nav = this.querySelector('[data-services-nav]');
    this.prevButton = this.querySelector('[data-services-prev]');
    this.nextButton = this.querySelector('[data-services-next]');

    if (!this.track || !this.nav || !this.prevButton || !this.nextButton) {
      console.warn('services-slider: разметка неполная, слайдер не инициализирован', this);
      return;
    }

    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.syncControls = this.syncControls.bind(this);

    this.lastWheelTime = Number.NEGATIVE_INFINITY;
    this.wheelCooldownUntil = 0;
    this.wheelAccumulated = 0;
    this.wheelAxis = WHEEL_AXIS_VERTICAL;

    this.prevButton.addEventListener('click', this.handlePrev);
    this.nextButton.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('wheel', this.handleWheel, { passive: false });

    this.resizeObserver = new ResizeObserver(this.syncControls);
    this.resizeObserver.observe(this.track);

    this.syncControls();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  handlePrev() {
    this.scrollToAdjacentCard(-DIRECTION_NEXT);
  }

  handleNext() {
    this.scrollToAdjacentCard(DIRECTION_NEXT);
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.syncControls();
    });
  }

  /**
   * Вертикальное колесо над треком листает карточки; на краях трека событие
   * отдаётся странице. Lenis глобально перехватывает wheel c preventDefault и
   * рвал бы жесты над треком, поэтому события, которые обрабатываем сами или
   * отдаём нативной прокрутке, экранируем от него через stopPropagation
   * (его листенер висит на window в bubble-фазе). События на краях, наоборот,
   * пропускаем к Lenis: нативный проброс deltaY из snap-контейнера на страницу
   * в Chromium ненадёжен, а Lenis скроллит страницу сам.
   */
  handleWheel(event) {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= SCROLL_EPSILON) return;

    // Ось жеста фиксируем по первому событию, иначе дрожание пальцев на тачпаде
    // посреди горизонтального свайпа переключало бы карусель на шаги по карточкам.
    if (event.timeStamp - this.lastWheelTime > WHEEL_GESTURE_GAP_MS) {
      this.wheelAxis =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? WHEEL_AXIS_HORIZONTAL : WHEEL_AXIS_VERTICAL;
      this.wheelAccumulated = 0;
    }
    this.lastWheelTime = event.timeStamp;

    // Горизонтальный жест крутит трек нативно, скрипт лишь прячет его от Lenis.
    if (this.wheelAxis === WHEEL_AXIS_HORIZONTAL) {
      event.stopPropagation();
      return;
    }

    const delta = normalizeWheelDelta(event);
    if (delta === 0) return;

    const direction = delta > 0 ? DIRECTION_NEXT : -DIRECTION_NEXT;
    const inCooldown = event.timeStamp < this.wheelCooldownUntil;

    if (!this.canScrollInDirection(direction)) {
      // Край трека: колесо уходит Lenis и листает страницу,
      // но хвост инерции сразу после шага карусели гасим.
      if (inCooldown) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (inCooldown) return;

    if (Math.sign(this.wheelAccumulated) !== Math.sign(delta)) this.wheelAccumulated = 0;
    this.wheelAccumulated += delta;
    if (Math.abs(this.wheelAccumulated) < WHEEL_STEP_THRESHOLD_PX) return;

    this.wheelAccumulated = 0;
    this.wheelCooldownUntil = event.timeStamp + WHEEL_STEP_COOLDOWN_MS;
    this.scrollToAdjacentCard(direction);
  }

  /** @param {number} direction 1 — вперёд, -1 — назад */
  canScrollInDirection(direction) {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    return direction === DIRECTION_NEXT
      ? this.track.scrollLeft < maxScroll - SCROLL_EPSILON
      : this.track.scrollLeft > SCROLL_EPSILON;
  }

  /** Левые границы карточек в системе координат прокрутки трека. */
  get cardOffsets() {
    const trackLeft = this.track.getBoundingClientRect().left;
    const scrolled = this.track.scrollLeft;

    return Array.from(this.track.children, (card) =>
      Math.round(card.getBoundingClientRect().left - trackLeft + scrolled)
    );
  }

  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Двигает трек к ближайшей карточке в заданном направлении.
   * @param {number} direction 1 — вперёд, -1 — назад
   */
  scrollToAdjacentCard(direction) {
    const offsets = this.cardOffsets;
    const scrolled = this.track.scrollLeft;

    const target =
      direction === DIRECTION_NEXT
        ? offsets.find((offset) => offset > scrolled + SCROLL_EPSILON)
        : offsets.reverse().find((offset) => offset < scrolled - SCROLL_EPSILON);

    if (target === undefined) return;

    this.track.scrollTo({ left: target, behavior: this.scrollBehavior });
  }

  syncControls() {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const isScrollable = maxScroll > SCROLL_EPSILON;
    const scrolled = this.track.scrollLeft;

    // Стрелки в макете стоят у заголовка всегда; когда листать нечего, они гаснут,
    // а не исчезают — иначе шапка секции прыгает между брейкпоинтами.
    this.nav.hidden = false;
    this.prevButton.disabled = scrolled <= SCROLL_EPSILON;
    this.nextButton.disabled = scrolled >= maxScroll - SCROLL_EPSILON;

    // Прокручиваемая область должна быть доступна с клавиатуры (WCAG 2.1.1),
    // но лишняя остановка в таб-порядке не нужна, когда прокручивать нечего.
    if (isScrollable) {
      this.track.setAttribute('tabindex', '0');
    } else {
      this.track.removeAttribute('tabindex');
    }
  }
}

if (!customElements.get('services-slider')) {
  customElements.define('services-slider', ServicesSlider);
}

})();
